import { Component, HostListener, QueryList, AfterContentInit, ViewChild, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, EventEmitter, Input, Output, ContentChildren, OnDestroy, Renderer2, ElementRef } from '@angular/core';
import { ButtonComponent, ButtonInfo } from '../button.component';
import { ButtonProxyComponent } from '../button-proxy/button-proxy.component';
import { SubScriptionMgr } from 'src/app/model/SubscriptionMgr';
import { ButtonGroupService } from 'src/app/services/button-group.service';
import { UUID } from 'angular2-uuid';

@Component({
  selector: 'app-button-set',
  templateUrl: './button-set.component.html',
  styleUrls: ['./button-set.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonSetComponent implements AfterViewInit, AfterContentInit, OnDestroy {
  @ContentChildren(ButtonComponent,{descendants:true}) buttons!: QueryList<ButtonComponent>;
  @ViewChild('proxy') proxy!: ButtonProxyComponent;
  @Output() positionUpdated = new EventEmitter<void>();

  private lookup!: Array<ButtonComponent>;
  private selectedIndex = 0;
  private focusedIndex = 0;
  private dropDownVis: boolean = false;

  private subscriptions = new SubScriptionMgr();
  private isDisabled = false;
  private isActiveOnly = false;
  public name = '';
  public groupName = '';

  @Input() set disabled(isDisabled: string) { this.isDisabled = isDisabled === 'true'; }
  get disabled() { return this.isDisabled ? 'true' : 'false'; }
  @Input() set showOnActivated(isAO: string) { this.isActiveOnly = isAO === 'true'; }
  get showOnActivated() { return this.isActiveOnly ? 'true' : 'false'; }
  @Input() set setName(name: string) {
    this.name = name.trim();
    this.groupName = name.trim();
    if (this.name !== '') { this.groups.RegisterButtonSet(name, this); }
  }
  private _isOn = false;
  @Input() set isOn(isOn: boolean) {
    this._isOn = isOn;
    if (!this.proxy) { window.requestAnimationFrame(() => { this.isOn = isOn; }); }
    this.proxy.button.isOn = isOn;
  }
  get isOn() { return this._isOn; }
  private _dropdownTitle = '';
  @Input('dropdown-title') set dropdownTitle(title) {
    this._dropdownTitle = title;
    this.cdr.markForCheck();
    this.Refresh();
  }
  get dropdownTitle() { return this._dropdownTitle; }
  public id = '';
  private _ready = false;
  constructor(private renderer: Renderer2, private domNode: ElementRef, private groups: ButtonGroupService, private cdr: ChangeDetectorRef) {
    this.id = UUID.UUID();
  }
  ngAfterContentInit(): void {
    this._ready = true;
    this.buttons.forEach((button: ButtonComponent, index: number) => {
      button.element.addEventListener('click', () => this.HandleButtonSelect(button));
      button.isOnChange.subscribe((isOn: boolean) => {
        this.groups.CloseAll();
        if (this.isActiveOnly && isOn) { this.HandleButtonSelect(button); }
        else if (button.id === this.proxy.buttonInfo?.guiId) { this.HandleButtonSelect(button); }
      });
    });
    this.Refresh();
  }
  ngAfterViewInit(): void {
    this.lookup = Array.from(this.buttons);
    let idx = -1;
    for (const btn of this.lookup) {
      idx++;
      const buttonIndex = idx;
      btn.element.addEventListener('keydown', (e: KeyboardEvent) => {
        this.HandleButtonKeyDown(e);
        this.HandleButtonTab(e, buttonIndex);
      });
      btn.element.addEventListener('focusin', () => this.focusedIndex = buttonIndex);
      if (idx === 0) { this.HandleButtonSelect(btn); this.selectedIndex = idx; }
      this.subscriptions.Subscribe(btn.change, (info: ButtonInfo) => {
        if (this.proxy.guidId === info.guiId) { this.proxy.set(info); }
      });
    }
    this.proxy.DropdownReady();
    this.proxy.button.element.addEventListener('keydown', (event: KeyboardEvent) => this.HandleProxyTab(event));
    this.subscriptions.Subscribe(this.proxy.click, (info: ButtonInfo) => {
      this.lookup.every((btn) => {
        if (btn.id === info.guiId) {
          if (btn.isToggleable) { btn.Toggle(this.proxy.button.isOn, false); }
          else { btn.Click(); }
        }
        return true;
      });
    });
    this.Refresh();
  }
  HandleButtonSelect(button: ButtonComponent) {
    const idx = this.lookup.indexOf(button);
    if (idx >= 0) { this.selectedIndex = idx; this.focusedIndex = idx; }
    this.proxy.set(button.Clone());
    if (!this.proxy.dropDownVis) { this.proxy.button.Focus(); } else { this.lookup[this.selectedIndex].Focus(); }
    this.Refresh();
  }
  HandleWheel(event: WheelEvent) {
    event.preventDefault();
    if (event.deltaY > 0) { this.SelectPrev(); } else { this.SelectNext(); }
  }
  SelectNext() { this.selectedIndex = (this.selectedIndex + 1) % this.lookup.length; this.HandleButtonSelect(this.lookup[this.selectedIndex]); }
  SelectPrev() { this.selectedIndex = (this.selectedIndex - 1 + this.lookup.length) % this.lookup.length; this.HandleButtonSelect(this.lookup[this.selectedIndex]); }
  ngOnDestroy() { if (this.name !== '') { this.groups.UnRegisterButtonSet(this.name); } }
  Close() { this.dropDownVis = false; this.proxy.Close(); this.Refresh(); }
  HandleDropDownVisibility(isOpen: boolean) { this.dropDownVis = isOpen; this.UpdatePosition(); if (isOpen) { requestAnimationFrame(() => this.FocusSelectedButton()); } }
  UpdatePosition() { this.proxy.Resized(); this.Refresh(); this.positionUpdated.emit(); }
  Refresh() { if (!this._ready) return; if (this.cdr) this.cdr.detectChanges(); }
  ToggleButton(name: string, isOn: boolean) { this.buttons.forEach(btn => { if (btn.isOn !== isOn && btn.buttonName === name) btn.Toggle(isOn); }); }
  ToggleButtons(names: Array<string>, isOn: boolean) { this.buttons.forEach(btn => { if (names.includes(btn.buttonName) && btn.isOn !== isOn) btn.Toggle(isOn); }); }
  FocusSelectedButton() { const btn = this.lookup[this.selectedIndex]; if (btn) { btn.Focus(); this.focusedIndex = this.selectedIndex; } }
  FocusPreviousSplitGroupButton(event: KeyboardEvent): boolean {
    const splitGroup = this.domNode.nativeElement.closest('.split-group') as HTMLElement;
    if (!splitGroup) return false;

    const buttons = Array.from(splitGroup.querySelectorAll('button')) as HTMLElement[];
    const currentButton = event.target as HTMLElement;
    const currentIndex = buttons.findIndex((button) => button === currentButton);
    if (currentIndex < 0) return false;

    for (let index = currentIndex - 1; index >= 0; index--) {
      const previousButton = buttons[index];
      const style = window.getComputedStyle(previousButton);
      if (style.display !== 'none' && style.visibility !== 'hidden'
        && !previousButton.closest('.hidden, .stateful')
        && !previousButton.hasAttribute('disabled') && previousButton.getAttribute('aria-disabled') !== 'true') {
        event.preventDefault();
        event.stopPropagation();
        previousButton.focus();
        return true;
      }
    }

    return false;
  }
  HandleProxyTab(event: KeyboardEvent) {
    if (event.key !== 'Tab' || !event.shiftKey || this.dropDownVis) return;

    if (this.proxy.IsToggleControl(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      this.proxy.button.Focus();
      return;
    }

    this.FocusPreviousSplitGroupButton(event);
  }
  HandleButtonTab(event: KeyboardEvent, buttonIndex: number) {
    if (event.key !== 'Tab' || !this.dropDownVis) return;

    const isTabbingPastLast = !event.shiftKey && buttonIndex === this.lookup.length - 1;
    const isShiftTabbingBeforeFirst = event.shiftKey && buttonIndex === 0;
    if (!isTabbingPastLast && !isShiftTabbingBeforeFirst) return;

    event.preventDefault();
    event.stopPropagation();
    this.Close();
    this.proxy.FocusToggle();
  }
  @HostListener('keydown', ['$event']) HandleGlobalKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab' || !this.dropDownVis || !this.proxy.dropdown) return;

    const target = event.target as HTMLElement;
    if (!this.proxy.dropdown.contains(target)) return;

    const last = this.lookup.length - 1;
    if (!event.shiftKey && this.focusedIndex >= last) {
      event.preventDefault();
      this.Close();
      this.proxy.FocusToggle();
    }
  }
  @HostListener('focusin', ['$event']) trapFocus(event: FocusEvent) {
    if (!this.dropDownVis) return;

    const target = event.target as HTMLElement;
    if (this.proxy.IsProxyControl(target)) return;

    if (this.proxy.dropdown.contains(target)) {
      const focusedIndex = this.lookup.findIndex((button) => button.element.contains(target));
      if (focusedIndex >= 0) this.focusedIndex = focusedIndex;
      return;
    }

    event.preventDefault();
    this.proxy.button.Focus();
  }
  HandleButtonKeyDown(event: KeyboardEvent) {
    if (event.key.startsWith('Arrow') && this.proxy.HasNonArrowKeyDown()) return;

    if (event.key === 'Tab' && event.shiftKey && !this.dropDownVis) {
      if (this.proxy.IsToggleControl(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        this.proxy.button.Focus();
        return;
      }
      this.FocusPreviousSplitGroupButton(event);
      return;
    }

    switch (event.key) {
      case 'ArrowLeft': this.SelectPrev(); event.preventDefault(); event.stopPropagation(); break;
      case 'ArrowRight': this.SelectNext(); event.preventDefault(); event.stopPropagation(); break;
      case 'ArrowUp':
      case 'ArrowDown':
        if (this.dropDownVis) {
          if (event.key === 'ArrowDown') this.SelectNext(); else this.SelectPrev();
          event.preventDefault(); event.stopPropagation(); this.FocusSelectedButton();
        }
        break;
      case 'Escape': this.Close(); event.preventDefault(); event.stopPropagation(); break;
    }
  }
}
